import type { ApiErrorEnvelope, FieldError } from './types'

/**
 * Codici di errore su cui il client **fa** qualcosa di diverso, oltre a mostrare il
 * messaggio (docs/05-errori.md). Per tutto il resto vale `error.message`: è il
 * comportamento che funziona anche con i codici che il pannello non conosce.
 */
export const ERROR_CODES = {
  // sessione
  unauthorized: 'unauthorized',
  accessTokenExpired: 'access_token_expired',
  invalidCredentials: 'invalid_credentials',
  refreshTokenInvalid: 'refresh_token_invalid',
  refreshTokenReused: 'refresh_token_reused',
  accountDisabled: 'account_disabled',
  forbidden: 'forbidden',
  // tenancy (TEN-04)
  organizationNotFound: 'organization_not_found',
  organizationForbidden: 'organization_forbidden',
  organizationSuspended: 'organization_suspended',
  insufficientPermissions: 'insufficient_permissions',
  membershipNotFound: 'membership_not_found',
  // verifica in due passaggi (OPS-04)
  mfaRequired: 'mfa_required',
  mfaInvalidCode: 'mfa_invalid_code',
  mfaSetupRequired: 'mfa_setup_required',
  mfaAlreadyEnabled: 'mfa_already_enabled',
  mfaNotEnabled: 'mfa_not_enabled',
  // generici
  badRequest: 'bad_request',
  notFound: 'not_found',
  conflict: 'conflict',
  preconditionFailed: 'precondition_failed',
  validationFailed: 'validation_failed',
  rateLimited: 'rate_limited',
  // admin
  exerciseInUse: 'exercise_in_use',
  importAlreadyRunning: 'import_already_running',
  importNotCancellable: 'import_not_cancellable',
  cannotModifySelf: 'cannot_modify_self',
  upstreamUnavailable: 'upstream_unavailable',
} as const

/** Errore restituito dall'API, con l'envelope già estratto. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown> | null
  readonly requestId: string | undefined
  /** Secondi da attendere, da `Retry-After`. Presente sui `429`. */
  readonly retryAfterSeconds: number | undefined

  constructor(init: {
    status: number
    code: string
    message: string
    details?: Record<string, unknown> | null
    requestId?: string
    retryAfterSeconds?: number
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.details = init.details ?? null
    this.requestId = init.requestId
    this.retryAfterSeconds = init.retryAfterSeconds
  }

  /** Errori per campo di `validation_failed`, da agganciare ai controlli del form. */
  get fieldErrors(): FieldError[] {
    const fields = this.details?.['fields']
    if (!Array.isArray(fields)) return []
    return fields.filter(
      (f): f is FieldError =>
        typeof f === 'object' &&
        f !== null &&
        typeof (f as FieldError).field === 'string' &&
        typeof (f as FieldError).message === 'string',
    )
  }

  /** Numero di schede che citano l'esercizio, da `exercise_in_use`. */
  get cardCount(): number | undefined {
    const value = this.details?.['cardCount']
    return typeof value === 'number' ? value : undefined
  }

  /** Id del job già in corso, da `import_already_running`. */
  get jobId(): string | undefined {
    const value = this.details?.['jobId']
    return typeof value === 'string' ? value : undefined
  }

  /**
   * Buono con cui completare il login, da `401 mfa_required`.
   *
   * Sta in `details` e non in un corpo di risposta proprio perché
   * `mfa_required` **non** è un errore dell'utente: è uno stato del flusso, e il
   * contract lo trasporta con l'unica busta che ha.
   */
  get mfaChallenge(): { mfaToken: string; expiresIn: number } | undefined {
    const token = this.details?.['mfaToken']
    if (typeof token !== 'string' || token === '') return undefined
    const expiresIn = this.details?.['expiresIn']
    return { mfaToken: token, expiresIn: typeof expiresIn === 'number' ? expiresIn : 0 }
  }

  /**
   * L'operazione richiede il secondo fattore e l'account non ce l'ha.
   *
   * Distinto da `mfa_required`, che riguarda il login: qui l'utente è già dentro
   * e la risposta è «vai a configurarlo», non «inserisci il codice». Il backend
   * lo restituisce sulle scritture amministrative e — con
   * `mfa.enforceForAdmins` — sull'intero perimetro di piattaforma.
   */
  get isMfaSetupRequired(): boolean {
    return this.code === ERROR_CODES.mfaSetupRequired
  }

  /** La sessione non è recuperabile: serve un nuovo login. */
  get isSessionLost(): boolean {
    return (
      this.code === ERROR_CODES.refreshTokenInvalid ||
      this.code === ERROR_CODES.refreshTokenReused ||
      this.code === ERROR_CODES.accountDisabled
    )
  }

  /** La risorsa è cambiata sotto i piedi: rileggere e riproporre la modifica. */
  get isStale(): boolean {
    return this.status === 412 || this.code === ERROR_CODES.preconditionFailed
  }

  /**
   * L'organizzazione selezionata non è (più) utilizzabile da questo account.
   *
   * Vale per tutte e tre le cause — non è più membro, l'id non esiste, la
   * palestra è sospesa — perché la reazione del pannello è la stessa: la
   * selezione conservata va buttata, altrimenti **ogni** richiesta successiva
   * continua a mandare quell'header e il pannello resta bloccato senza che
   * l'operatore possa capire perché.
   */
  get isOrganizationRejected(): boolean {
    return (
      this.code === ERROR_CODES.organizationForbidden ||
      this.code === ERROR_CODES.organizationNotFound ||
      this.code === ERROR_CODES.organizationSuspended
    )
  }
}

/** Errore di rete o di parsing: la richiesta non è mai arrivata a destinazione. */
export class NetworkError extends Error {
  constructor(message: string, cause?: unknown) {
    // `cause` è già un campo standard di `Error`: si passa al costruttore invece di
    // dichiararne uno che lo nasconderebbe.
    super(message, cause !== undefined ? { cause } : undefined)
    this.name = 'NetworkError'
  }
}

function isEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (typeof value !== 'object' || value === null) return false
  const candidate = (value as ApiErrorEnvelope).error
  return (
    typeof candidate === 'object' &&
    candidate !== null &&
    typeof candidate.code === 'string' &&
    typeof candidate.message === 'string'
  )
}

/**
 * Costruisce un `ApiError` da una risposta non riuscita.
 *
 * Un backend che risponde 502 con l'HTML di un reverse proxy non produce l'envelope:
 * in quel caso si sintetizza un codice per status, così la UI ha comunque qualcosa
 * di sensato da dire invece di "undefined".
 */
export async function toApiError(response: Response): Promise<ApiError> {
  const requestId = response.headers.get('X-Request-Id') ?? undefined
  const retryAfter = Number.parseInt(response.headers.get('Retry-After') ?? '', 10)

  let body: unknown = null
  try {
    const text = await response.text()
    if (text) body = JSON.parse(text)
  } catch {
    // Corpo assente o non-JSON: si procede col fallback per status.
  }

  if (isEnvelope(body)) {
    return new ApiError({
      status: response.status,
      code: body.error.code,
      message: body.error.message,
      details: body.error.details ?? null,
      requestId: body.error.requestId ?? requestId,
      ...(Number.isFinite(retryAfter) ? { retryAfterSeconds: retryAfter } : {}),
    })
  }

  return new ApiError({
    status: response.status,
    code: fallbackCode(response.status),
    message: fallbackMessage(response.status),
    requestId,
    ...(Number.isFinite(retryAfter) ? { retryAfterSeconds: retryAfter } : {}),
  })
}

function fallbackCode(status: number): string {
  switch (status) {
    case 400:
      return 'bad_request'
    case 401:
      return ERROR_CODES.unauthorized
    case 403:
      return ERROR_CODES.forbidden
    case 404:
      return ERROR_CODES.notFound
    case 409:
      return ERROR_CODES.conflict
    case 412:
      return ERROR_CODES.preconditionFailed
    case 422:
      return ERROR_CODES.validationFailed
    case 429:
      return ERROR_CODES.rateLimited
    case 503:
      return 'service_unavailable'
    default:
      return status >= 500 ? 'internal_error' : 'bad_request'
  }
}

function fallbackMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Richiesta non valida.'
    case 401:
      return 'Accesso richiesto.'
    case 403:
      return 'Operazione non consentita.'
    case 404:
      return 'Risorsa non trovata.'
    case 409:
      return 'Operazione in conflitto con lo stato attuale.'
    case 412:
      return 'La risorsa è stata modificata. Ricarica e riprova.'
    case 422:
      return 'Alcuni campi non sono validi.'
    case 429:
      return 'Troppi tentativi. Riprova tra poco.'
    case 503:
      return 'Servizio temporaneamente non disponibile.'
    default:
      return status >= 500 ? 'Si è verificato un errore. Riprova.' : 'Richiesta non valida.'
  }
}

/** Messaggio da mostrare per un errore qualsiasi, non solo per un `ApiError`. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof NetworkError) return error.message
  if (error instanceof Error) return error.message
  return 'Si è verificato un errore. Riprova.'
}
